import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import DOMPurify from 'dompurify';
import { UserContext } from './types';
import $routes from 'sitewide-js/core_pre_webpack/routes';

/**
 * Sanitizes user input to prevent XSS attacks
 * Removes all HTML tags and returns plain text
 */
const sanitizeInput = (input: string): string => {
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
  }).trim();
};

export const getUserContext = async (): Promise<UserContext> => {
  const response = await fetchWithCsrf(
    $routes.api_v0_army_of_grad_students_user_context_path({
      subdomain_param: 'api',
    }),
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
  );

  if (!response.ok) {
    throw new Error('Failed to fetch user context');
  }

  const data = await response.json();
  return data.user_context;
};

export const createUserContext = async (
  entry_point: string,
): Promise<UserContext> => {
  const response = await fetchWithCsrf(
    $routes.api_v0_army_of_grad_students_user_context_path({
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        entry_point: entry_point,
      }),
    },
  );

  if (!response.ok) {
    throw new Error('Failed to create user context');
  }

  const data = await response.json();
  return data.user_context;
};

export const updateUserContext = async (
  updates: Partial<UserContext>,
): Promise<UserContext> => {
  const response = await fetchWithCsrf(
    $routes.api_v0_army_of_grad_students_user_context_path({
      subdomain_param: 'api',
    }),
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_context: updates,
      }),
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.error ||
        errorData.errors?.join(', ') ||
        'Failed to update user context',
    );
  }

  const data = await response.json();
  return data.user_context;
};

export const submitFeedback = async (
  feedback: string,
): Promise<UserContext> => {
  const newFeedbackObject = {
    date: new Date().toISOString(),
    text: feedback,
  };
  return await updateUserContext({ feedback: [newFeedbackObject] });
};

export const loadMoreRecommendations = async (): Promise<{
  recommendation_count: number;
}> => {
  const response = await fetchWithCsrf(
    $routes.load_more_recommendations_api_v0_army_of_grad_students_user_context_path(
      {
        subdomain_param: 'api',
      },
    ),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to load more recommendations');
  }

  const data = await response.json();
  return {
    recommendation_count: data.recommendation_count,
  };
};

export type TopicEvaluationResponse = {
  quality: 'high_quality' | 'low_quality';
  message?: string;
  research_area_id?: number;
  session_id?: number;
  user_interest_id?: number;
  user_context_id?: number;
};

/**
 * Evaluates a research topic for quality
 * @param researchTopic - The research topic to evaluate (will be sanitized)
 * @returns The evaluation result including quality and associated IDs
 */
export const evaluateTopic = async (
  researchTopic: string,
): Promise<TopicEvaluationResponse> => {
  const sanitizedTopic = sanitizeInput(researchTopic);

  if (!sanitizedTopic) {
    throw new Error('Research topic is required');
  }

  const response = await fetchWithCsrf(
    $routes.api_v0_army_of_grad_students_topic_evaluation_evaluate_topic_path({
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ research_topic: sanitizedTopic }),
    },
  );

  if (!response.ok) {
    throw new Error('Failed to evaluate topic');
  }

  return await response.json();
};
